import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense, Component, type ReactNode } from 'react'
import Lottie from 'lottie-react'
import { AuthProvider } from '@/contexts/AuthContext'
import { PermissionProvider } from '@/contexts/PermissionContext'
import { ErrorProvider, ErrorNotifications } from '@/contexts/ErrorContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { OfflineBanner } from '@/components/OfflineBanner'
import { LoginPage } from '@/pages/LoginPage'
import { usePWAUpdate, PWAUpdateModal, hardReload } from '@/hooks/usePWAUpdate'
import truckAnimation from '@/animations/truck-loading.json'

// Logo path (from public folder)
const GPC_LOGO = '/2020.05.20-GPC_logo-transbg-3508px.png'

const ShiftsView = lazy(() => import('@/pages/ShiftsView').then(m => ({ default: m.ShiftsView })))

/** Component that monitors for PWA updates and shows centered modal */
function PWAUpdateNotifier() {
  const { showModal, handleRefresh } = usePWAUpdate();
  return <PWAUpdateModal show={showModal} onRefresh={handleRefresh} />;
}

// Error boundary to catch chunk loading failures (stale cache after deployment)
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError(error: Error) {
    // Chunk loading error (Chrome / Firefox / Safari wording)
    if (error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk') ||
        error.message.includes('Importing a module script failed')) {
      return { hasError: true }
    }
    throw error // Re-throw non-chunk errors
  }

  // A chunk failed to load because the deployed build moved on. Reloading is
  // only useful if it escapes the old service worker's precache, so this uses
  // the same hard reload as the update modal — the previous version fired
  // reload() without waiting for the unregister, so the old worker was still
  // in control and served the same stale index.html straight back.
  handleReload = hardReload

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'var(--color-bg-primary)',
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'var(--color-bg-secondary)',
            borderRadius: '12px',
            maxWidth: '400px',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
            <h2 style={{ color: 'var(--color-text-primary)', marginBottom: '12px' }}>
              New Version Available
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px' }}>
              The app has been updated. Please reload to get the latest version.
            </p>
            <button
              onClick={this.handleReload}
              style={{
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload App
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

function LoadingFallback() {
  return (
    <div style={{
      background: 'linear-gradient(180deg, var(--color-bg-primary), var(--color-bg-secondary))',
      minHeight: '100vh',
      padding: '16px',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        padding: '60px 20px',
        minHeight: '60vh',
      }}>
        {/* Company Logo with glow effect */}
        <div style={{ animation: 'logoGlow 2s ease-in-out infinite' }}>
          <img
            src={GPC_LOGO}
            alt="GPC - Gardermoen Perishable Center"
            style={{
              maxWidth: '380px',
              width: '100%',
              height: 'auto',
              filter: 'drop-shadow(0 4px 12px rgba(0, 51, 204, 0.3))',
            }}
          />
        </div>

        {/* Truck Animation */}
        <div style={{ width: '200px', height: '100px' }}>
          <Lottie
            animationData={truckAnimation}
            loop={true}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        {/* Loading Text */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}>
            Loading shifts...
          </div>
          <div style={{
            color: 'var(--color-text-tertiary)',
            fontSize: '13px',
            animation: 'pulse 2s ease-in-out infinite',
          }}>
            Preparing your schedule
          </div>
        </div>

        {/* Loading bar */}
        <div style={{
          width: '240px',
          height: '4px',
          background: 'var(--color-bg-tertiary)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: '40%',
            height: '100%',
            background: 'linear-gradient(90deg, var(--color-info), var(--color-info))',
            borderRadius: '2px',
            animation: 'loadingBar 1.5s ease-in-out infinite',
          }} />
        </div>
      </div>
      <style>{`
        @keyframes logoGlow {
          0%, 100% { filter: drop-shadow(0 4px 12px rgba(0, 51, 204, 0.3)); }
          50% { filter: drop-shadow(0 4px 20px rgba(0, 102, 204, 0.4)); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes loadingBar {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(150%); }
          100% { transform: translateX(150%); }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <ErrorProvider>
          <OfflineBanner />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingFallback />}>
                        <ShiftsView />
                      </Suspense>
                    </ChunkErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route path="/shifts" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <PWAUpdateNotifier />
          <ErrorNotifications />
        </ErrorProvider>
      </PermissionProvider>
    </AuthProvider>
  )
}

export default App
